import {
  IAcceptPartProTradeBid,
  IPartProTradeBid,
  IPartProTradeBidLot,
  IPartProTradeBidVehicleDetail,
} from "../models/partPro/IPartProTradeBids";

/** Same key the pricing matrix uses for its cells. */
export const matrixCellKey = (partCategoryId: string, accountId: string) =>
  `${partCategoryId}-${accountId}`;

export interface IPendingMatrixBids {
  /** Bids from an account on a category it prices, keyed by matrix cell - highest first */
  byCell: Record<string, IPartProTradeBid[]>;
  /**
   * Bids on a matrix row with no cell of their own: a buyer with no Core account, or an account
   * not set up for that category. Keyed by part category id.
   */
  otherByCategory: Record<string, IPartProTradeBid[]>;
  /** The category (PartPro lot) each waiting bid is on */
  lotIdByBidId: Record<string, string>;
  /** Every bid still waiting for a decision, including categories the matrix doesn't show */
  bidCount: number;
  categoryCount: number;
}

/**
 * Lays a vehicle's PartPro bids over its pricing matrix. Only undecided categories count: an
 * accepted bid is already the cell's price, and a passed one is over.
 */
export function groupPendingMatrixBids(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  enabledCellKeys: Set<string>
): IPendingMatrixBids {
  const result: IPendingMatrixBids = { byCell: {}, otherByCategory: {}, lotIdByBidId: {}, bidCount: 0, categoryCount: 0 };

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0)
    .forEach((lot) => {
      result.bidCount += lot.bids.length;
      result.categoryCount += 1;
      lot.bids.forEach((bid) => (result.lotIdByBidId[bid.id] = lot.id));
      if (!lot.partCategoryId) return;
      const categoryId = lot.partCategoryId;

      lot.bids.forEach((bid) => {
        const key = bid.accountId ? matrixCellKey(categoryId, bid.accountId) : null;
        if (key && enabledCellKeys.has(key)) (result.byCell[key] ??= []).push(bid);
        else (result.otherByCategory[categoryId] ??= []).push(bid);
      });
    });

  return result;
}

/** Bids picked to accept, keyed by lot - PartPro awards one bid per category. */
export type BidPicks = Record<string, string>;

/** Where the user changed the default pick, keyed by lot: another bid, or null to leave it out. */
export type BidPickChoices = Record<string, string | null>;

/**
 * The bids sharing a waiting category's top amount, or none when one bid is clearly highest.
 * PartPro would break the tie on who bid first, but that is not a reason to pick a buyer, so a
 * tie is never picked for you - someone has to choose.
 */
export function tiedTopBids(lot: IPartProTradeBidLot): IPartProTradeBid[] {
  if (lot.status !== "PENDING" || lot.bids.length < 2) return [];
  const top = Math.max(...lot.bids.map((bid) => bid.amount));
  const tied = lot.bids.filter((bid) => bid.amount === top);
  return tied.length > 1 ? tied : [];
}

export interface ITiedMatrixBids {
  /** Lots on the matrix whose top amount is shared */
  lotIds: Set<string>;
  /** The bids sharing each of those top amounts */
  bidIds: Set<string>;
}

/** The tied categories on the matrix - same rows highestBidPicks considers. */
export function tiedMatrixBids(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  shownCategoryIds: Set<string>
): ITiedMatrixBids {
  const result: ITiedMatrixBids = { lotIds: new Set(), bidIds: new Set() };

  detail?.lots
    .filter((lot) => !!lot.partCategoryId && shownCategoryIds.has(lot.partCategoryId))
    .forEach((lot) => {
      const tied = tiedTopBids(lot);
      if (tied.length === 0) return;
      result.lotIds.add(lot.id);
      tied.forEach((bid) => result.bidIds.add(bid.id));
    });

  return result;
}

/** Tied categories nobody has chosen a bid on yet - these block nothing else, but aren't accepted. */
export const unresolvedTieCount = (ties: ITiedMatrixBids, picks: BidPicks): number =>
  Array.from(ties.lotIds).filter((lotId) => !picks[lotId]).length;

/**
 * The highest bid on each waiting category shown on the matrix - what gets accepted unless the
 * user picks otherwise. A category where the top amount is tied is left unpicked.
 */
export function highestBidPicks(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  shownCategoryIds: Set<string>
): BidPicks {
  const picks: BidPicks = {};

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0)
    .filter((lot) => !!lot.partCategoryId && shownCategoryIds.has(lot.partCategoryId))
    .filter((lot) => tiedTopBids(lot).length === 0)
    .forEach((lot) => {
      picks[lot.id] = lot.bids.reduce((best, bid) => (bid.amount > best.amount ? bid : best)).id;
    });

  return picks;
}

/** The highest bid still waiting on each category, by part category id. */
export function highestPendingBidByCategoryId(
  detail: IPartProTradeBidVehicleDetail | null | undefined
): Map<string, number> {
  const highest = new Map<string, number>();

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0 && !!lot.partCategoryId)
    .forEach((lot) => {
      const top = Math.max(...lot.bids.map((bid) => bid.amount));
      highest.set(lot.partCategoryId!, Math.max(highest.get(lot.partCategoryId!) ?? 0, top));
    });

  return highest;
}

/** Categories already sold on PartPro Trade - an accepted bid that hasn't been cancelled. */
export const awardedCategoryIds = (detail: IPartProTradeBidVehicleDetail | null | undefined): Set<string> =>
  new Set(
    (detail?.lots ?? [])
      .filter((lot) => lot.status === "AWARDED" && !!lot.partCategoryId)
      .map((lot) => lot.partCategoryId!)
  );

/**
 * The picks to accept: the highest bids with the user's choices laid over them. A choice whose
 * bid was withdrawn, or whose category was decided, since it was made is dropped.
 */
export function resolveBidPicks(highest: BidPicks, choices: BidPickChoices, pending: IPendingMatrixBids): BidPicks {
  const picks: BidPicks = {};

  Object.entries(highest).forEach(([lotId, bidId]) => {
    if (pending.lotIdByBidId[bidId] === lotId) picks[lotId] = bidId;
  });

  Object.entries(choices).forEach(([lotId, bidId]) => {
    if (bidId === null) delete picks[lotId];
    else if (pending.lotIdByBidId[bidId] === lotId) picks[lotId] = bidId;
  });

  return picks;
}

/** Clicking a bid: picks it in place of its category's current pick, or un-picks it. */
export function toggleBidPick(picks: BidPicks, choices: BidPickChoices, lotId: string, bidId: string): BidPickChoices {
  return { ...choices, [lotId]: picks[lotId] === bidId ? null : bidId };
}

/** True when the picks are exactly the highest bids - nothing changed or left out. */
export function isHighestPicks(picks: BidPicks, highest: BidPicks): boolean {
  const lots = Object.keys(highest);
  return lots.length === Object.keys(picks).length && lots.every((lotId) => picks[lotId] === highest[lotId]);
}

export const picksToAccept = (picks: BidPicks): IAcceptPartProTradeBid[] =>
  Object.entries(picks).map(([lotId, bidId]) => ({ lotId, bidId }));

/** Who a bid is from, preferring the Core account name. */
export const bidderName = (bid: IPartProTradeBid) => bid.accountName ?? bid.buyerCompanyName;
